import { Stack, Typography, useTheme } from '@mui/material';

import { sessionDrawerSectionLabelSx } from '@kangaroo/ui';

import type { LapTimingMode, useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import type { CompareSessionGroups, SessionListItem } from '../../features/sessions/sessionList';
import { SessionSelectMenu } from './SessionSelectMenu';
import { SessionsLapTable } from './SessionsLapTable';

type SessionLapPanelProps = {
  heading?: string;
  sessionSelectLabel?: string;
  sessions?: SessionListItem[];
  sessionGroups?: CompareSessionGroups;
  sessionId?: string | null;
  onSessionChange?: (sessionId: string) => void;
  sessionLoading?: boolean;
  lapTimingMode: LapTimingMode;
  slot: ReturnType<typeof useSessionLapSlot>;
  mychronLapNumber: number | null;
  onMychronLapChange: (lapNumber: number) => void;
  showSessionSelect?: boolean;
  showLapTable?: boolean;
  selectionMode?: 'primary' | 'compare';
  footageCountsByLap?: Record<number, number>;
};

export function SessionLapPanel({
  heading,
  sessionSelectLabel,
  sessions,
  sessionGroups,
  sessionId = null,
  onSessionChange,
  sessionLoading = false,
  lapTimingMode,
  slot,
  mychronLapNumber,
  onMychronLapChange,
  showSessionSelect = true,
  showLapTable = true,
  selectionMode = 'primary',
  footageCountsByLap,
}: SessionLapPanelProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const selectedLapNumber =
    lapTimingMode === 'circuit' ? slot.selectedLapNumber : mychronLapNumber;
  const activeLaps = lapTimingMode === 'circuit' ? slot.circuitLaps : slot.laps;

  const flatSessions = sessions ?? [
    ...(sessionGroups?.matching ?? []),
    ...(sessionGroups?.other ?? []),
  ];

  const handleLapSelect = (lapNumber: number) => {
    if (lapTimingMode === 'circuit') {
      slot.circuitLapReslice.setSelectedLapNumber(lapNumber);
      onMychronLapChange(lapNumber);
      return;
    }

    onMychronLapChange(lapNumber);
  };

  const lapTableLoading =
    Boolean(sessionId)
    && activeLaps.length === 0
    && (sessionLoading || (lapTimingMode === 'circuit' && slot.contributionsLoading));

  const emptyLabel =
    lapTimingMode === 'circuit'
      ? sessionId
        ? slot.hasCircuitOverlay
          ? 'Regenerate circuit laps in setup.'
          : 'Set up a circuit to slice laps.'
        : 'Choose a session first.'
      : sessionId
        ? 'No raw laps in this session.'
        : 'Choose a session first.';

  return (
    <Stack spacing={1}>
      {heading ? (
        <Typography sx={sessionDrawerSectionLabelSx(isDark)}>
          {heading}
        </Typography>
      ) : null}

      {showSessionSelect && sessionSelectLabel && onSessionChange ? (
        <SessionSelectMenu
          label={sessionSelectLabel}
          value={sessionId}
          sessions={sessions}
          sessionGroups={sessionGroups}
          onChange={onSessionChange}
          disabled={flatSessions.length === 0}
        />
      ) : null}

      {showLapTable ? (
        <SessionsLapTable
          laps={activeLaps}
          selectedLapNumber={selectedLapNumber}
          selectionMode={selectionMode}
          loading={lapTableLoading}
          emptyLabel={emptyLabel}
          footageCountsByLap={footageCountsByLap}
          onSelect={handleLapSelect}
        />
      ) : null}
    </Stack>
  );
}
